
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Package,
  ShoppingCart,
  Users,
  FolderTree,
  Layers,
  MapPin,
  TrendingUp,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";

export default function AdminDashboard() {
  const { token } = useAuthStore();
  const [stats, setStats] = useState({
    total_products: 0,
    total_categories: 0,
    total_collections: 0,
    total_users: 0,
    total_orders: 0,
    total_wishlist_items: 0,
    location_analytics: {
      by_state: [],
      by_city: [],
      by_pincode: [],
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchDashboardStats();
    }
  }, [token]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getDashboardStats(token!);
      // API function already handles wrapped response
      setStats({
        total_products: data.total_products || 0,
        total_categories: data.total_categories || 0,
        total_collections: data.total_collections || 0,
        total_users: data.total_users || 0,
        total_orders: data.total_orders || 0,
        total_wishlist_items: data.total_wishlist_items || 0,
        location_analytics: data.location_analytics || {
          by_state: [],
          by_city: [],
          by_pincode: [],
        },
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Products",
      value: stats.total_products,
      icon: Package,
      color: "bg-blue-500",
      href: "/admin/products",
    },
    {
      title: "Total Categories",
      value: stats.total_categories,
      icon: FolderTree,
      color: "bg-green-500",
      href: "/admin/categories",
    },
    {
      title: "Total Collections",
      value: stats.total_collections,
      icon: Layers,
      color: "bg-purple-500",
      href: "/admin/collections",
    },
    {
      title: "Total Users",
      value: stats.total_users,
      icon: Users,
      color: "bg-pink-500",
      href: "/admin/users",
    },
    {
      title: "Total Orders",
      value: stats.total_orders,
      icon: ShoppingCart,
      color: "bg-orange-500",
      href: "/admin/orders",
    },
    {
      title: "Wishlist Items",
      value: stats.total_wishlist_items,
      icon: Package,
      color: "bg-cyan-500",
      href: "/admin/wishlist",
    },
  ];

  if (loading) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <div className="h-7 w-48 bg-gray-200 rounded-lg animate-pulse mb-2" />
          <div className="h-4 w-64 bg-gray-200 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-8 bg-gray-200 rounded-md animate-pulse" />
              </div>
              <div className="h-6 w-16 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3" />
              <div className="space-y-2">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-12 bg-gray-200 rounded-lg animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold font-playfair mb-1">Dashboard</h1>
        <p className="text-sm text-gray-600">Welcome to the admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Link key={card.title} to={card.href}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200"
              >
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-600 font-medium">{card.title}</p>
                    <div className={`${card.color} p-1.5 rounded-md`}>
                      <Icon className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{card.value}</p>
                </div>
              </motion.div>
            </Link>
          );
        })}
      </div>

      {/* Location Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Orders by State */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-purple-600" />
            <h2 className="text-sm font-bold text-gray-900">Orders by State</h2>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.location_analytics.by_state && stats.location_analytics.by_state.length > 0 ? (
              stats.location_analytics.by_state.map((item: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">{item.state || "Unknown"}</p>
                    <p className="text-xs text-gray-500">{item.count} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-900">₹{item.total_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </motion.div>

        {/* Orders by City */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-bold text-gray-900">Orders by City</h2>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.location_analytics.by_city && stats.location_analytics.by_city.length > 0 ? (
              stats.location_analytics.by_city.map((item: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">{item.city || "Unknown"}</p>
                    <p className="text-xs text-gray-500">{item.state ? `${item.state} • ` : ""}{item.count} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-900">₹{item.total_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </motion.div>

        {/* Orders by Pincode */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-green-600" />
            <h2 className="text-sm font-bold text-gray-900">Orders by Pincode</h2>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.location_analytics.by_pincode && stats.location_analytics.by_pincode.length > 0 ? (
              stats.location_analytics.by_pincode.map((item: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">{item.pincode || "Unknown"}</p>
                    <p className="text-xs text-gray-500">
                      {item.city ? `${item.city}` : ""}
                      {item.state ? `, ${item.state}` : ""} • {item.count} orders
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-900">₹{item.total_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
      >
        <h2 className="text-sm font-bold mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Link
            to="/admin/products/new"
            className="text-left px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
          >
            <p className="font-semibold text-xs text-purple-900">Add New Product</p>
            <p className="text-xs text-purple-600">Create a new product listing</p>
          </Link>
          <Link
            to="/admin/orders"
            className="text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <p className="font-semibold text-xs text-blue-900">View All Orders</p>
            <p className="text-xs text-blue-600">Manage customer orders</p>
          </Link>
          <Link
            to="/admin/products"
            className="text-left px-3 py-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
          >
            <p className="font-semibold text-xs text-green-900">Manage Products</p>
            <p className="text-xs text-green-600">Update inventory and details</p>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
